// ─────────────────────────────────────────────────────────────────────────────
// Data layer for /questionnaires — pure RPC.
//
// Rules:
//   1. Each function makes ONE HTTP call and returns the raw response.
//   2. No param encoding, no JSON building, no offset arithmetic — callers pass
//      already-built query strings / bodies.
//   3. No response normalisation, no `{ data: ... }` unwrap — return the raw
//      shape; hooks normalise.
//
// Everything else (param→qs encoding, request body assembly, response
// normalisation) lives in the corresponding `-hooks/use*.ts` file.
// ─────────────────────────────────────────────────────────────────────────────

import type {
  BulkCreateQuestionnaireRequestsBody,
  UpdateQuestionnaireBody,
} from '@retracedgmbh/modules/questionnaires/contracts';
import type {
  QuestionnaireRequestsParams,
  QuestionnaireRequestsResponse,
  QuestionnaireTemplatesResponse,
  QuestionnaireTemplateBasic as SharedQuestionnaireTemplate,
} from '@/api/questionnaires/types';
import { API_ROOT_URL, BACKEND_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  ArchiveQuestionnairesPayload,
  BuilderDetailEnvelope,
  CreatedQuestionnaire,
  CreateQuestionnairePayload,
  CreateTemplatePayload,
  DataPoint,
  DataPointTemplateGroup,
  DeleteQuestionnairesPayload,
  ImpactAreaHierarchyNode,
  MyQuestionnaireRequestsResponse,
  MyTemplatesResponse,
  QuestionAssetLinksResponse,
  QuestionnaireResponsesResponse,
  QuestionnairesResponse,
  QuestionnaireTemplatesListResponse,
  UnarchiveQuestionnairesPayload,
} from './types';

/** Requests the acting company sent to one other company — the company
 *  profile's Questionnaires tab.
 *
 *  Migrated to the modules architecture, so the path and the base both changed.
 *  Leaving QUESTIONNAIRE_SERVICE_URL here would resolve to
 *  `…/supplier-questionnaire-service/questionnaires/requests` and 404. */
export async function fetchQuestionnaireRequests(
  params: QuestionnaireRequestsParams,
): Promise<QuestionnaireRequestsResponse> {
  const qs = new URLSearchParams();
  qs.set('companyId', params.companyId);
  qs.set('page', String(params.page - 1));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return apiFetch<QuestionnaireRequestsResponse>(
    `/questionnaires/requests?${qs.toString()}`,
    {},
    BACKEND_URL,
  );
}

export async function fetchQuestionnaireTemplates(): Promise<
  SharedQuestionnaireTemplate[]
> {
  const qs = new URLSearchParams({
    page: '0',
    filter: JSON.stringify({ isTemplate: 0, isArchived: 0 }),
    count: '500',
    isTemplate: 'false',
  });
  const res = await apiFetch<QuestionnaireTemplatesResponse>(
    `/questionnaires?${qs.toString()}`,
    {},
    BACKEND_URL,
  );
  return res.data;
}

/** Invites companies onto a questionnaire — one answer-request row per item.
 *
 *  Backend answers 204; non-2xx throws via `apiFetch` so the caller surfaces a
 *  toast. 409 means at least one selected company already has a live request for
 *  this questionnaire, and the server rejects the whole batch in that case.
 *
 *  Migrated to the modules architecture, so the path, the base and the body all
 *  changed: the questionnaire id moved into the path and the bare array is now
 *  wrapped in `{ requests }`. Leaving QUESTIONNAIRE_SERVICE_URL here would
 *  resolve to `…/supplier-questionnaire-service/questionnaires/…` and 404. */
export async function bulkCreateQuestionnaireRequests(
  questionnaireId: string,
  body: BulkCreateQuestionnaireRequestsBody,
): Promise<void> {
  await apiFetch<void>(
    `/questionnaires/${questionnaireId}/requests`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    BACKEND_URL,
  );
}

// ─── Questionnaires list ────────────────────────────────────────────────────

export async function fetchQuestionnaires(
  qs: string,
): Promise<QuestionnairesResponse> {
  return apiFetch<QuestionnairesResponse>(
    `/questionnaires?${qs}`,
    {},
    BACKEND_URL,
  );
}

/** Per-questionnaire request list — one row per company that received
 *  this questionnaire, with per-request status + answer statistics.
 *  Backs the Summary subtab's companies table and the Review subtab's
 *  master rail.
 *
 *  Migrated to the modules architecture, so both the path and the base
 *  changed. Leaving QUESTIONNAIRE_SERVICE_URL here would resolve to
 *  `…/supplier-questionnaire-service/questionnaires/…` and 404. */
export async function fetchQuestionnaireResponsesList(
  questionnaireId: string,
  qs: string,
): Promise<QuestionnaireResponsesResponse> {
  return apiFetch<QuestionnaireResponsesResponse>(
    `/questionnaires/${questionnaireId}/requests?${qs}`,
    {},
    BACKEND_URL,
  );
}

/** Returns the raw `{ data }` envelope — `useCreateQuestionnaire` unwraps it. */
export async function createQuestionnaire(
  payload: CreateQuestionnairePayload,
): Promise<{ data: CreatedQuestionnaire }> {
  return apiFetch<{ data: CreatedQuestionnaire }>(
    '/questionnaires',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export async function archiveQuestionnaires(
  payload: ArchiveQuestionnairesPayload,
): Promise<void> {
  await apiFetch<void>(
    '/questionnaires/archive',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export async function unarchiveQuestionnaires(
  payload: UnarchiveQuestionnairesPayload,
): Promise<void> {
  await apiFetch<void>(
    '/questionnaires/unarchive',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

export async function deleteQuestionnaires(
  payload: DeleteQuestionnairesPayload,
): Promise<void> {
  await apiFetch<void>(
    '/questionnaires',
    { method: 'DELETE', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

// Bulk operations on per-company answer requests (Responses → Summary
// page). All four endpoints scope by `questionnaireId` + a list of
// `requestIds`. Only the due-date path still mirrors the legacy service; the
// delete and reminder have been migrated (see below).
interface BulkRequestArgs {
  questionnaireId: string;
  requestIds: string[];
}

/** Removes the selected companies' answer requests. 204 on success.
 *
 *  Migrated to the modules architecture, so the path, the base and the body all
 *  changed: the questionnaire id moved into the path and only `requestIds`
 *  remains in the body. Leaving QUESTIONNAIRE_SERVICE_URL here would resolve to
 *  `…/supplier-questionnaire-service/questionnaires/…` and 404.
 *
 *  The `BulkRequestArgs` signature is kept because the two still-legacy siblings
 *  share it and every caller passes the same pair — only the wire format moved. */
export async function bulkDeleteAnswerRequests({
  questionnaireId,
  requestIds,
}: BulkRequestArgs): Promise<void> {
  await apiFetch<void>(
    `/questionnaires/${questionnaireId}/requests`,
    { method: 'DELETE', body: JSON.stringify({ requestIds }) },
    BACKEND_URL,
  );
}

/** Moves the deadline on the selected companies' answer requests. 204 on
 *  success.
 *
 *  Migrated to the modules architecture, so the path, the base and the body all
 *  changed: the questionnaire id moved into the path and only `requestIds` and
 *  `dueDate` remain in the body. Leaving QUESTIONNAIRE_SERVICE_URL here would
 *  resolve to `…/supplier-questionnaire-service/questionnaires/…` and 404.
 *
 *  `dueDate` is a required `YYYY-MM-DD` string, not `string | null`. It was typed
 *  nullable, but null was never accepted — the legacy body schema declared
 *  `{type:'string', format:'date'}`, so sending null 400'd. Clearing a due date
 *  is not a product action: the date editor commits on pick and the bulk-bar Save
 *  is disabled until a day is chosen. Requests that are already completed are
 *  accepted and skipped server-side, so a selection containing them succeeds
 *  without moving their date. */
export async function bulkUpdateAnswerRequestsDueDate({
  questionnaireId,
  requestIds,
  dueDate,
}: BulkRequestArgs & { dueDate: string }): Promise<void> {
  await apiFetch<void>(
    `/questionnaires/${questionnaireId}/requests/due-date`,
    { method: 'PATCH', body: JSON.stringify({ requestIds, dueDate }) },
    BACKEND_URL,
  );
}

/** Reminds the selected companies that their answers are outstanding. 204 on
 *  success.
 *
 *  Migrated to the modules architecture, so the path, the base and the body all
 *  changed: the questionnaire id moved into the path and only `requestIds`
 *  remains in the body. Leaving QUESTIONNAIRE_SERVICE_URL here would resolve to
 *  `…/supplier-questionnaire-service/questionnaires/…` and 404.
 *
 *  An empty `requestIds` still means "remind every request on the
 *  questionnaire". Requests that are already completed are accepted and skipped
 *  server-side, so a selection containing them succeeds without notifying them. */
export async function sendAnswerRequestsReminder({
  questionnaireId,
  requestIds,
}: BulkRequestArgs): Promise<void> {
  await apiFetch<void>(
    `/questionnaires/${questionnaireId}/requests/reminder`,
    { method: 'PATCH', body: JSON.stringify({ requestIds }) },
    BACKEND_URL,
  );
}

export async function exportAnswerRequests(args: {
  questionnaireId: string;
  /** When true, the server ignores `requestIds` and exports every
   *  request on the questionnaire. */
  allRequests: boolean;
  requestIds: string[];
}): Promise<void> {
  // Migrated to the modules architecture — the path is now `/api/...` on the
  // main gateway, so the base must be API_ROOT_URL. Leaving
  // QUESTIONNAIRE_SERVICE_URL here would resolve to
  // `…/supplier-questionnaire-service/api/questionnaires/…` and 404.
  await apiFetch<void>(
    `/api/questionnaires/${args.questionnaireId}/export`,
    {
      method: 'POST',
      body: JSON.stringify({
        allRequests: args.allRequests,
        requestIds: args.requestIds,
      }),
    },
    API_ROOT_URL,
  );
}

// Migrated to the modules architecture: the route now lives on the main
// gateway, so both the path and the base change. The response is the standard
// `{ data }` envelope — the previous `Questionnaire` return type was never what
// this endpoint sent; it returns the questionnaire entity, not a list row.
export async function cloneQuestionnaireAsTemplate(
  id: string,
): Promise<{ data: CreatedQuestionnaire }> {
  return apiFetch<{ data: CreatedQuestionnaire }>(
    `/questionnaires/${id}/save-as-template`,
    { method: 'POST' },
    BACKEND_URL,
  );
}

/** The shared template pool — the company's own plus the active Retraced ones.
 *
 *  Migrated to the modules architecture, so the path and the base both changed.
 *  `isTemplate=true` is not optional: the legacy `/v1/templates` route always
 *  answered with templates, while `/api/questionnaires` serves the regular
 *  questionnaire list unless the mode is stated. Dropping it returns 200 with
 *  the wrong rows rather than an error. */
export async function fetchQuestionnaireTemplatesList(): Promise<QuestionnaireTemplatesListResponse> {
  return apiFetch<QuestionnaireTemplatesListResponse>(
    '/questionnaires?isTemplate=true',
    {},
    BACKEND_URL,
  );
}

// ─── Requests ───────────────────────────────────────────────────────────────

/** The acting company's inbox — every request addressed to it.
 *
 *  Same endpoint as `fetchQuestionnaireRequests`, minus `companyId`: without
 *  that param the backend flips from "requests I sent" to "requests I
 *  received". Migrated to the modules architecture, so the path and the base
 *  both changed. */
export async function fetchMyQuestionnaireRequests(
  qs: string,
): Promise<MyQuestionnaireRequestsResponse> {
  return apiFetch<MyQuestionnaireRequestsResponse>(
    `/questionnaires/requests?${qs}`,
    {},
    BACKEND_URL,
  );
}

// ─── Templates ──────────────────────────────────────────────────────────────

/** Migrated to the modules architecture, so the path and the base both changed.
 *  Callers must put `myTemplates=true` (own only) or `isTemplate=true` (shared
 *  pool) in `qs` — `/api/questionnaires` serves the regular questionnaire list
 *  when neither is present, which the legacy `/v1/templates` route could never
 *  do. */
export async function fetchMyTemplates(
  qs: string,
): Promise<MyTemplatesResponse> {
  return apiFetch<MyTemplatesResponse>(
    `/questionnaires?${qs}`,
    {},
    BACKEND_URL,
  );
}

/** Migrated to the modules architecture, so the path and the base both changed.
 *
 *  The response is the standard `{ data }` envelope — the previous
 *  `QuestionnaireTemplate` return type was never what this endpoint sent; it
 *  returns the questionnaire entity, not a list row. */
export async function createQuestionnaireTemplate(
  payload: CreateTemplatePayload,
): Promise<{ data: CreatedQuestionnaire }> {
  return apiFetch<{ data: CreatedQuestionnaire }>(
    '/questionnaires/templates',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

// `duplicateQuestionnaireTemplate` (`POST /v1/templates/:id/copy`) was retired
// rather than repointed: the legacy copy route and
// `POST /questionnaires/:id/save-as-template` were two URLs onto the same
// service function, so after the cutover it would have been a byte-for-byte
// duplicate of `cloneQuestionnaireAsTemplate` above. The templates tab now calls
// that one instead.

/**
 * Migrated to the modules architecture, so the path, the base and the body all
 * changed: the legacy route took a bare id array, the new one takes a named
 * `templateIds` field — matching the questionnaires bulk delete.
 *
 * Still 204 with no body, so `apiFetch` resolves `undefined`.
 */
export async function deleteQuestionnaireTemplates(
  ids: string[],
): Promise<void> {
  await apiFetch<void>(
    '/questionnaires/templates',
    { method: 'DELETE', body: JSON.stringify({ templateIds: ids }) },
    BACKEND_URL,
  );
}

// ─── Builder ────────────────────────────────────────────────────────────────

export type { BuilderDetailEnvelope } from './types';

/**
 * One route for both modes. The legacy `GET /v1/templates/:id` was retired as a
 * duplicate: its handler and the migrated builder read both called the same
 * `getBuilderDetails`, and the lookup has never filtered `is_template`, so
 * `/api/questionnaires/:id/builder` already answers a template.
 *
 * `mode` is therefore gone from this signature but still matters upstream, where
 * it keys the cache in `useBuilderDetailQuery`.
 */
export async function fetchBuilderDetail(
  id: string,
): Promise<BuilderDetailEnvelope> {
  return apiFetch<BuilderDetailEnvelope>(
    `/api/questionnaires/${id}/builder`,
    {},
    API_ROOT_URL,
  );
}

/**
 * One route for both modes here too, now that the legacy `PUT /v1/templates/:id`
 * has been retired: it was the same write path as the migrated builder save, its
 * handler differing only in forcing the template's `dueDate` to null — which the
 * module service now does off the stored `is_template` flag.
 *
 * `mode` is therefore gone from this signature as well. Templates send no due
 * date, and the endpoint takes `dueDate` as optional-and-nullable to accept that.
 * Answers a `{ data }` envelope; `useSaveBuilder` unwraps it.
 */
export async function saveBuilderDetail(
  id: string,
  body: Record<string, unknown>,
): Promise<BuilderDetailEnvelope> {
  return apiFetch<BuilderDetailEnvelope>(
    `/api/questionnaires/${id}/builder`,
    { method: 'PUT', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

// ─── Question Library ───────────────────────────────────────────────────────

/** PATCH a batch of datapoint answers back to the company profile under
 *  a single template (e.g. `WASTE`, `ENERGY_SOURCES`, `ABOUT`). The
 *  questionnaire-side "Sync profile data" button fires one of these per
 *  template grouping. Mirrors the legacy `patchCompanyProfiles` call. */
export async function syncTemplateAnswersToProfile(
  templateId: string,
  body: {
    answer: Record<string, unknown>;
    forCompanyId: string;
    byCompanyId: string;
    onBehalfOfCompanyId: string;
    meta: {
      source: {
        from: 'questionnaire-sync';
        id: string;
        forCompanyId: string;
        requestId: string;
      };
    };
  },
): Promise<void> {
  // The template-answer route lives on the main API gateway
  // (`/api/companies/templates/:templateId/answer`), NOT on the
  // companies-service microservice — mirrors `patchProfileTemplate` in
  // `@/api/companies`. Hitting `COMPANIES_SERVICE_URL` + `/v1/templates/...`
  // 404s with `Route PATCH:/companies-service/v1/templates/.../answer not found`.
  await apiFetch<void>(
    `/api/companies/templates/${templateId.toLowerCase()}/answer`,
    { method: 'PATCH', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

// Lives on the main API gateway (`/api/companies/impact-areas/hierarchy/:sectionId`),
// NOT on the companies-service microservice — mirrors `patchProfileTemplate`.
export async function fetchImpactAreasHierarchy(
  sectionId: number,
): Promise<ImpactAreaHierarchyNode[]> {
  const res = await apiFetch<ImpactAreaHierarchyNode[]>(
    `/api/companies/impact-areas/hierarchy/${sectionId}`,
    {},
    API_ROOT_URL,
  );
  return res ?? [];
}

/** Data points grouped one entry per profile template — the question library
 *  tree. Was `/v1/data-points?groupByTemplates=true` on the questionnaire
 *  service; the flag is now its own migrated route, which answers the
 *  `{ metadata, data }` envelope rather than a bare array. */
export async function fetchDataPointTemplates(): Promise<
  DataPointTemplateGroup[]
> {
  const res = await apiFetch<{ data: DataPointTemplateGroup[] }>(
    '/api/questionnaires/data-points/templates',
    {},
    API_ROOT_URL,
  );
  return res?.data ?? [];
}

/** Flat list of every data-point definition. Used by the preview page to
 *  resolve options for datapoint-bound questions whose options were not
 *  inlined on save — covers datapoints that aren't part of any impact-area
 *  template (e.g. network / connection-type datapoints) and therefore
 *  don't appear in the template-grouped response.
 *
 *  Definitions carry `retracedId`, not `id` — only the template-grouped route
 *  adds that alias. */
export async function fetchAllDataPoints(): Promise<DataPoint[]> {
  const res = await apiFetch<{ data: DataPoint[] }>(
    '/api/questionnaires/data-points',
    {},
    API_ROOT_URL,
  );
  return res?.data ?? [];
}

// ─── Extra datapoint option sources (preview-only) ──────────────────────────
//
// A handful of legacy datapoints (`facility_processes`, `product_types`,
// `product_levels`, `relationship_type`) resolve their option lists from
// dedicated endpoints rather than the datapoint definition. The preview
// page calls these to backfill option pickers that would otherwise come
// up empty.

interface FacilityProcessesResponse {
  data?: Array<{
    productionProcessId?: string;
    productionProcessName?: string;
    children?: Array<{ key: string; text: string }>;
  }>;
}

export async function fetchFacilityProcessOptions(): Promise<
  Array<{ label: string; id: string; group?: string }>
> {
  // `apiFetch` hands us the parsed `{ data, metadata }` body verbatim, so the
  // grouped rows are under `data`.
  const res = await apiFetch<FacilityProcessesResponse>(
    '/api/facility-processes/dropdown',
    {},
    API_ROOT_URL,
  );
  const facilities = res?.data ?? [];
  const out: Array<{ label: string; id: string; group?: string }> = [];
  for (const facility of facilities) {
    const group = facility.productionProcessName ?? undefined;
    for (const child of facility.children ?? []) {
      out.push({
        id: child.key,
        label: child.text,
        ...(group ? { group } : {}),
      });
    }
  }
  return out;
}

interface ProductCategoriesResponse {
  // `reply.paginate(...)` puts the rows at top-level `data`.
  data?: Array<{
    id: string;
    name: string;
    parentId?: string | null;
  }>;
}

export async function fetchProductCategoryOptions(): Promise<{
  productTypes: Array<{ label: string; id: string }>;
  productLevels: Array<{ label: string; id: string }>;
}> {
  const res = await apiFetch<ProductCategoriesResponse>(
    '/api/product-categories',
    {},
    API_ROOT_URL,
  );
  const rows = res?.data ?? [];
  return {
    productTypes: rows
      .filter(r => r.parentId)
      .map(r => ({ id: r.id, label: r.name })),
    productLevels: rows
      .filter(r => !r.parentId)
      .map(r => ({ id: r.id, label: r.name })),
  };
}

interface RelationshipTypesResponse {
  data?: Array<{ id: string; name: string }>;
}

export async function fetchRelationshipTypeOptions(): Promise<
  Array<{ label: string; id: string }>
> {
  const res = await apiFetch<RelationshipTypesResponse>(
    '/api/companies/relationship-types',
    {},
    API_ROOT_URL,
  );
  return (res?.data ?? []).map(r => ({ id: r.id, label: r.name }));
}

// ─── Asset type options (DOCUMENTS / AUDIT / CERTIFICATE) ───────────────────

export async function fetchAssetTypeOptions(): Promise<QuestionAssetLinksResponse> {
  return apiFetch<QuestionAssetLinksResponse>(
    '/questionnaires/question-asset-links',
    {},
    BACKEND_URL,
  );
}

// ─── Questionnaire status ────────────────────────────────────────────────────

export async function patchQuestionnaireStatus(
  id: string,
  status: NonNullable<UpdateQuestionnaireBody['status']>,
): Promise<void> {
  await apiFetch<void>(
    `/questionnaires/${id}`,
    { method: 'PATCH', body: JSON.stringify({ status }) },
    BACKEND_URL,
  );
}

// The bulk-invite endpoint lives with the other questionnaire-request calls —
// see `bulkCreateQuestionnaireRequests` above. It used to be duplicated here as
// `bulkInviteCompanies`, byte-identical to `sendQuestionnaireInvites`, which is
// exactly how one copy gets repointed and the other left 404ing.
